/**
 * useDevices - TanStack Query Hook
 *
 * Hook pour la gestion des appareils de connexion
 * ISO 27001 A.9.4.2 - Procédures de connexion sécurisée
 *
 * Pattern: Component → Hook (TanStack Query) → Service (API)
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { devicesAPI, LoginDevice } from '@/utils/api';
import { logger } from '@/utils/logger';
import { getErrorMessage } from '@/lib/errorHandler';
import { notifications } from '@/lib/notifications';

// ==================== QUERY KEYS ====================

export const devicesKeys = {
  all: ['devices'] as const,
  list: () => [...devicesKeys.all, 'list'] as const,
};

// ==================== TYPES ====================

interface DevicesListResponse {
  devices: LoginDevice[];
  currentDeviceId: string | null;
}

// ==================== QUERIES ====================

/**
 * Liste des appareils de l'utilisateur
 */
export function useDevices() {
  return useQuery<DevicesListResponse>({
    queryKey: devicesKeys.list(),
    queryFn: async () => {
      const response = await devicesAPI.listDevices();
      logger.log('Devices loaded:', response.devices.length, 'Current device:', response.currentDeviceId);
      return response;
    },
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

// ==================== MUTATIONS ====================

/**
 * Marquer un appareil comme de confiance
 */
export function useTrustDevice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ deviceId, days = 90 }: { deviceId: string; days?: number }) => {
      await devicesAPI.trustDevice(deviceId, days);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: devicesKeys.all });
    },
    onError: (error) => {
      logger.error('Failed to trust device:', error);
      notifications.error(getErrorMessage(error));
    },
  });
}

/**
 * Révoquer la confiance d'un appareil
 */
export function useRevokeDevice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (deviceId: string) => {
      await devicesAPI.revokeDevice(deviceId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: devicesKeys.all });
    },
    onError: (error) => {
      logger.error('Failed to revoke device:', error);
      notifications.error(getErrorMessage(error));
    },
  });
}

/**
 * Supprimer un appareil
 */
export function useDeleteDevice() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (deviceId: string) => {
      await devicesAPI.deleteDevice(deviceId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: devicesKeys.all });
    },
    onError: (error) => {
      logger.error('Failed to delete device:', error);
      notifications.error(getErrorMessage(error));
    },
  });
}

/**
 * Révoquer tous les appareils de confiance
 */
export function useRevokeAllDevices() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const result = await devicesAPI.revokeAllDevices();
      logger.log(`Revoked ${result.revokedCount} devices`);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: devicesKeys.all });
    },
    onError: (error) => {
      logger.error('Failed to revoke all devices:', error);
      notifications.error(getErrorMessage(error));
    },
  });
}
